import React, { useMemo, useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { TopBar } from '../../components/TopBar/TopBar';
import { ProjectDetailViewModel } from '../../viewModels/ProjectDetailViewModel';
import { ProjectProgress } from '../../shared/models/ProjectProgress';
import { DonationModal } from '../../components/DonationModal/DonationModal';
import { FundraisingType, Project } from '../../shared/models/Project';
import { Donation } from '../../shared/models/Donation';
import { DonationTeam } from '../../shared/models/DonationTeam';
import { ViewMoreButton } from '../../components/ViewMoreButton/ViewMoreButton';
import { ProgressTimeline, ProgressTimelineItem } from '../../components/ProgressTimeline';
import { formatRelativeTime } from '../../utils/dateFormat';
import './ProjectDetailView.css';

interface Props {
  viewModel: ProjectDetailViewModel;
  onBack: () => void;
  onGoHome: () => void;
  onProgressClick?: (progress: ProjectProgress) => void;
  onDonate?: (params: { amount: number; recurring: boolean; anonymous: boolean; teamId?: string }, project?: Project) => void;
  onOrganizationClick?: (organizationId: string, organization?: any) => void;
  onAgreementClick?: () => void;
  onStartTeam?: (project: Project) => void;
  onOpenMyTeams?: (project: Project, tab: 'initiated' | 'joined') => void;
  onJoinTeam?: (team: DonationTeam, project: Project) => Promise<boolean> | boolean;
  onTeamClick?: (team: DonationTeam, project: Project) => void;
  hideContent?: boolean; // 隐藏主要内容，只保留 modal
  hideModal?: boolean; // 隐藏 modal（当显示协议页面时）
}

const placeholder = '/placeholder.svg';

export const ProjectDetailView: React.FC<Props> = observer(({ viewModel, onBack, onGoHome, onProgressClick, onDonate, onOrganizationClick, onAgreementClick, onStartTeam, onOpenMyTeams, onJoinTeam, onTeamClick, hideContent = false, hideModal = false }) => {
  const { project, loading, error } = viewModel;
  const [foldDetail, setFoldDetail] = useState(true);
  const [foldProgress, setFoldProgress] = useState(true);
  const isStopped = project?.status === 'STOPPED';
  const [donateVisible, setDonateVisible] = useState(false);
  const [selectedTeamForDonate, setSelectedTeamForDonate] = useState<DonationTeam | null>(null);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return dateStr.length >= 10 ? dateStr.slice(0, 10) : dateStr;
  };

  const detailText = project?.detail || '';
  const detailRef = useRef<HTMLDivElement>(null);
  // 判断是否需要显示"查看更多"按钮（通过检查内容高度）
  const [showDetailMore, setShowDetailMore] = useState(false);
  
  // 项目进展初始显示数量
  const initialProgressCount = 3;
  const displayedProgresses = foldProgress && viewModel.progresses.length > initialProgressCount
    ? viewModel.progresses.slice(0, initialProgressCount)
    : viewModel.progresses;

  // 检查项目详情内容是否需要折叠
  useEffect(() => {
    if (detailRef.current && detailText) {
      const checkOverflow = () => {
        const el = detailRef.current;
        if (el) {
          // 临时移除折叠类，检查完整高度
          const wasFolded = el.classList.contains('fold');
          if (wasFolded) {
            el.classList.remove('fold');
          }
          const fullHeight = el.scrollHeight;
          if (wasFolded) {
            el.classList.add('fold');
          }
          const foldedHeight = 200; // 与CSS中的max-height一致
          setShowDetailMore(fullHeight > foldedHeight);
        }
      };
      
      // 延迟检查，确保DOM已渲染
      const timer = setTimeout(checkOverflow, 100);
      return () => clearTimeout(timer);
    }
  }, [detailText, foldDetail]);

  return (
    <div className="project-detail-page">
      <div className="pd-wrapper" style={{ display: hideContent ? 'none' : 'block' }}>
        <TopBar title={project?.title || '项目详情'} showBack onBack={onBack} />

        {loading && <div className="pd-loading">加载中...</div>}
        {error && <div className="pd-error">{error}</div>}

        {project && (
          <>
            <div className="pd-hero">
              <img
                src={project.bigImageUrl || project.coverImageUrl || placeholder}
                alt={project.title}
                onError={(e) => {
                  e.currentTarget.src = placeholder;
                }}
                className={isStopped ? 'stopped' : ''}
              />
            </div>

            <div className="pd-card pd-basic">
              <div className="pd-title">{project.title || '未命名项目'}</div>
              <div className="pd-desc">{project.description || '暂无简介'}</div>
              {project.publicFundraisingNumber && (
                <div className="pd-row">备案编号：{project.publicFundraisingNumber}</div>
              )}
            </div>

            <div className="pd-stats">
              <div className="pd-stat-item">
                <div className="pd-stat-label">已筹款（元）</div>
                <div className="pd-stat-value">¥{(project.raisedAmount || 0).toLocaleString()}</div>
              </div>
              <div className="pd-stat-item">
                <div className="pd-stat-label">累计爱心（次）</div>
                <div className="pd-stat-value">{project.participantCount || 0}</div>
              </div>
            </div>

            <div className="pd-card">
              <div className="pd-section-title">
                <span>一起捐</span>
              </div>
              {viewModel.donationTeamsLoading && <div className="pd-placeholder">加载中...</div>}
              {!viewModel.donationTeamsLoading && viewModel.donationTeams.length === 0 && (
                <div className="pd-placeholder">暂无一起捐团队，快来发起吧</div>
              )}
              {viewModel.donationTeams.length > 0 && (
                <div className="pd-donation-list">
                  {viewModel.donationTeams.map((team) => (
                    <div
                      key={team.id}
                      className="pd-donation-item clickable"
                      onClick={() => onTeamClick?.(team, project)}
                    >
                      <div className="pd-donation-avatar">
                        {team.avatar ? (
                          <img src={team.avatar} alt={team.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <div className="pd-donation-avatar-placeholder">👥</div>
                        )}
                      </div>
                      <div className="pd-donation-content">
                        <div className="pd-donation-name">{team.name}</div>
                        <div className="pd-donation-time">
                          和{team.memberCount || 0}位小伙伴筹得{(team.donationAmount || 0).toFixed(2)}元
                        </div>
                      </div>
                      <button
                        className="pd-share-btn"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const joined = await onJoinTeam?.(team, project);
                          if (joined) {
                            setSelectedTeamForDonate(team);
                            setDonateVisible(true);
                          }
                        }}
                      >
                        跟TA一起捐
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pd-card">
              <div className="pd-section-title">
                <span>项目详情</span>
              </div>
              <div
                ref={detailRef}
                className={`pd-detail-text ${foldDetail ? 'fold' : ''}`}
                dangerouslySetInnerHTML={{ __html: detailText || '暂无详情' }}
              />
              {showDetailMore && (
                <ViewMoreButton
                  text={foldDetail ? '查看更多' : '收起'}
                  iconType={foldDetail ? 'down' : null}
                  onClick={() => setFoldDetail(!foldDetail)}
                  className="pd-detail-more"
                />
              )}
            </div>

            <div className="pd-card">
              <div className="pd-section-title">
                <span>进展报告</span>
              </div>
              {viewModel.progressLoading ? (
                <div className="pd-placeholder">加载中...</div>
              ) : viewModel.progresses.length === 0 ? (
                <ProgressTimeline>
                  <ProgressTimelineItem
                    date={formatDate(project?.publishTime || project?.createTime)}
                  >
                    <div className="pd-timeline-card">
                      <div className="pd-timeline-title">启动公益计划</div>
                    </div>
                  </ProgressTimelineItem>
                </ProgressTimeline>
              ) : (
                <ProgressTimeline>
                  {displayedProgresses.map((item: ProjectProgress) => {
                    // 虚拟进展（启动公益计划）不可点击
                    const isVirtual = item.id.startsWith('virtual-launch-');
                    return (
                      <ProgressTimelineItem
                        key={item.id}
                        date={formatDate(item.progressDate)}
                        onClick={isVirtual ? undefined : () => onProgressClick?.(item)}
                      >
                        <div className="pd-timeline-card">
                          <div className="pd-timeline-title">{item.title}</div>
                          {item.content && (
                            <div 
                              className="pd-timeline-desc" 
                              dangerouslySetInnerHTML={{ __html: item.content || '' }} 
                            />
                          )}
                          {item.images && item.images.length > 0 && (
                            <div className="pd-timeline-images">
                              {item.images.slice(0, 3).map((img, idx) => (
                                <img src={img} alt={item.title} key={idx} loading="lazy" />
                              ))}
                            </div>
                          )}
                        </div>
                      </ProgressTimelineItem>
                    );
                  })}
                  {/* 如果折叠且当前显示的数量少于总数，显示"查看更多"展开按钮 */}
                  {foldProgress && viewModel.progresses.length > initialProgressCount && (
                    <div className="pd-timeline-more">
                      <ViewMoreButton
                        text="查看更多"
                        onClick={() => setFoldProgress(false)}
                      />
                    </div>
                  )}
                  {/* 如果已展开，且还有更多数据，显示"加载更多"按钮 */}
                  {!foldProgress && viewModel.progressHasMore && !viewModel.progressLoading && (
                    <div className="pd-timeline-more">
                      <ViewMoreButton
                        text="加载更多"
                        onClick={() => viewModel.loadMoreProgresses()}
                      />
                    </div>
                  )}
                  {/* 如果已展开且没有更多数据，显示"没有更多了" */}
                  {!foldProgress && !viewModel.progressHasMore && viewModel.progresses.length > 0 && (
                    <div className="pd-timeline-more">
                      <p style={{ textAlign: 'center', color: '#999', fontSize: '14px', margin: '10px 0' }}>没有更多了</p>
                    </div>
                  )}
                </ProgressTimeline>
              )}
            </div>

            <div className="pd-card">
              <div className="pd-section-title">
                <span>募捐动态</span>
              </div>
              {viewModel.donationLoading && viewModel.donations.length === 0 && (
                <div className="pd-placeholder">加载中...</div>
              )}
              {!viewModel.donationLoading && viewModel.donations.length === 0 && (
                <div className="pd-placeholder">暂无募捐动态</div>
              )}
              {viewModel.donations.length > 0 && (
                <div className="pd-donation-list">
                  {viewModel.donations.map((donation: Donation) => (
                    <div key={donation.id} className="pd-donation-item">
                      <div className="pd-donation-avatar">
                        {donation.isAnonymous ? (
                          <div className="pd-donation-avatar-placeholder">👤</div>
                        ) : donation.donorAvatar ? (
                          <img 
                            src={donation.donorAvatar} 
                            alt={donation.donorNickname || '用户'} 
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const placeholder = e.currentTarget.parentElement?.querySelector('.pd-donation-avatar-placeholder');
                              if (placeholder) {
                                placeholder.classList.remove('hidden');
                              }
                            }}
                          />
                        ) : (
                          <div className="pd-donation-avatar-placeholder">👤</div>
                        )}
                        {!donation.isAnonymous && donation.donorAvatar && (
                          <div className="pd-donation-avatar-placeholder hidden">👤</div>
                        )}
                      </div>
                      <div className="pd-donation-content">
                        <div className="pd-donation-name">
                          {donation.isAnonymous ? '爱心用户' : (donation.donorNickname || '爱心人士')}
                        </div>
                        <div className="pd-donation-time">
                          {formatRelativeTime(donation.paymentTime || donation.createTime)}
                        </div>
                      </div>
                      <div className="pd-donation-amount">
                        {donation.donationType === 'MONTHLY' && '月捐·'}
                        {donation.amount}元
                      </div>
                    </div>
                  ))}
                  {viewModel.donationHasMore && (
                    <ViewMoreButton
                      onClick={() => viewModel.loadMoreDonations()}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="pd-card">
              <div className="pd-section-title">
                <span>机构信息</span>
              </div>
              {project.organization && (
                <div 
                  className="pd-org-item clickable" 
                  onClick={() => onOrganizationClick?.(project.organization!.id, project.organization)}
                >
                  <div className="pd-org-icon">
                    {project.organization.logoUrl ? (
                      <img src={project.organization.logoUrl} alt={project.organization.name} onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }} />
                    ) : (
                      <div className="pd-org-placeholder">🏢</div>
                    )}
                  </div>
                  <div className="pd-org-content">
                    <div className="pd-org-name">{project.organization.name}</div>
                    <div className="pd-org-type">善款接收机构</div>
                  </div>
                  <div className="pd-org-arrow">›</div>
                </div>
              )}
              {project.executingOrganization && (
                <div 
                  className="pd-org-item clickable" 
                  onClick={() => onOrganizationClick?.(project.executingOrganization!.id, project.executingOrganization)}
                >
                  <div className="pd-org-icon">
                    {project.executingOrganization.logoUrl ? (
                      <img src={project.executingOrganization.logoUrl} alt={project.executingOrganization.name} onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }} />
                    ) : (
                      <div className="pd-org-placeholder">🏢</div>
                    )}
                  </div>
                  <div className="pd-org-content">
                    <div className="pd-org-name">{project.executingOrganization.name}</div>
                    <div className="pd-org-type">执行机构</div>
                  </div>
                  <div className="pd-org-arrow">›</div>
                </div>
              )}
              {project.projectManager && (
                <div className="pd-info-row">
                  <div className="pd-info-label">项目负责人</div>
                  <div className="pd-info-value">{project.projectManager}</div>
                </div>
              )}
              {project.contactPhone && (
                <div className="pd-info-row">
                  <div className="pd-info-label">联系电话</div>
                  <div className="pd-info-value">{project.contactPhone}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="pd-bottom-bar">
        <div className="pd-bar-inner">
          <button className="pd-btn" onClick={onGoHome}>
            首页
          </button>
          <button
            className="pd-btn pd-btn-primary"
            disabled={isStopped}
            onClick={() => {
              if (isStopped || !project) return;
              onStartTeam?.(project);
            }}
          >
            发起一起捐
          </button>
          <button
            className="pd-btn pd-btn-primary"
            disabled={isStopped}
            onClick={() => {
              if (isStopped) return;
              setSelectedTeamForDonate(null);
              setDonateVisible(true);
            }}
          >
            {isStopped ? '已停止募捐' : '我要捐赠'}
          </button>
        </div>
      </div>

      <DonationModal
        visible={donateVisible && !hideModal}
        onClose={() => setDonateVisible(false)}
        onAgreementClick={onAgreementClick}
        onConfirm={({ amount, recurring, anonymous }) => {
          if (onDonate) {
            onDonate({ amount, recurring, anonymous, teamId: selectedTeamForDonate?.id }, project || undefined);
          } else {
            alert(
              `暂未接入支付流程，金额：${amount}${recurring ? '（每月一捐）' : ''}${anonymous ? '，匿名' : ''}`
            );
          }
        }}
        fundraisingType={project?.fundraisingType as FundraisingType | undefined}
        tiers={project?.fundraisingTiers}
        supportCustomAmount={project?.supportCustomAmount}
        customAmountMin={project?.customAmountMin}
        customAmountMax={project?.customAmountMax}
        projectTitle={project?.title}
      />
    </div>
  );
});

